import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  Banknote,
  Braces,
  Briefcase,
  Building,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Code2,
  Copy,
  Download,
  FileText,
  HelpCircle,
  Layers,
  Package,
  Pencil,
  Plus,
  Save,
  Scale,
  Sparkles,
  Star,
  Stethoscope,
  Trash2,
  Trophy,
  User,
  X,
} from 'lucide-react';

// Map from template icon key to the actual Lucide component — keeps the import
// list tree-shakeable (the schema template constants only carry string keys).
const ICONS = {
  Banknote,
  Braces,
  Briefcase,
  Building,
  Building2,
  Calendar,
  ChevronRight,
  Code2,
  FileText,
  HelpCircle,
  Package,
  Scale,
  Star,
  Stethoscope,
  Trophy,
  User,
};
const iconFor = (name) => ICONS[name] || Braces;
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import { toast } from '../../lib/toast';
import {
  CATEGORY_ORDER,
  SCHEMA_TEMPLATES,
  defaultValues,
  getTemplate,
  validateValues,
} from '../../constants/schemaTemplates';
import { formatRelative } from '../../lib/format';

// View state machine: 'list' (saved) | 'browse' (template picker) | 'edit' (form)
export default function SchemaTab({ domain }) {
  const qc = useQueryClient();
  const [view, setView] = useState('list');
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [editingId, setEditingId] = useState(null); // null = creating, else editing this saved id
  const [name, setName] = useState('');
  const [attachedUrl, setAttachedUrl] = useState('');
  const [values, setValues] = useState({});
  const [saveError, setSaveError] = useState('');

  const savedQuery = useQuery({
    queryKey: ['schema-templates', domain._id],
    queryFn: () =>
      api
        .get(`/api/schema-templates?domain_id=${domain._id}`)
        .then((r) => r.data.templates),
  });

  const saveMutation = useMutation({
    mutationFn: (payload) => {
      if (editingId) {
        return api.put(`/api/schema-templates/${editingId}`, payload).then((r) => r.data.template);
      }
      return api.post('/api/schema-templates', payload).then((r) => r.data.template);
    },
    onSuccess: (template) => {
      qc.invalidateQueries({ queryKey: ['schema-templates', domain._id] });
      const isCreate = !editingId;
      setEditingId(template._id);
      setSaveError('');
      toast.success(isCreate ? 'Schema created' : 'Schema saved');
    },
    onError: (err) => {
      setSaveError(err.response?.data?.error || 'Could not save schema');
      toast.error(err, 'Could not save schema');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/schema-templates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schema-templates', domain._id] });
      toast.success('Schema deleted');
    },
    onError: (err) => toast.error(err, 'Could not delete schema'),
  });

  const activeTemplate = activeTemplateId ? getTemplate(activeTemplateId) : null;
  const generatedJson = useMemo(() => {
    if (!activeTemplate) return '';
    try {
      const obj = activeTemplate.build(values);
      return JSON.stringify(obj, null, 2);
    } catch (err) {
      return `// Error: ${err.message}`;
    }
  }, [activeTemplate, values]);
  const errors = useMemo(
    () => (activeTemplate ? validateValues(activeTemplate, values) : []),
    [activeTemplate, values],
  );

  const startNew = (templateId) => {
    const tpl = getTemplate(templateId);
    setActiveTemplateId(templateId);
    setEditingId(null);
    setName(`${tpl.label} – ${domain.domain}`);
    setAttachedUrl('');
    setValues(defaultValues(tpl));
    setSaveError('');
    setView('edit');
  };

  const startEdit = (saved) => {
    setActiveTemplateId(saved.type);
    setEditingId(saved._id);
    setName(saved.name);
    setAttachedUrl(saved.attached_url || '');
    setValues({ ...defaultValues(getTemplate(saved.type) || { fields: [] }), ...(saved.values || {}) });
    setSaveError('');
    setView('edit');
  };

  const backToList = () => {
    setView('list');
    setActiveTemplateId(null);
    setEditingId(null);
  };

  const onSave = () => {
    if (errors.length) return;
    saveMutation.mutate({
      name: name.trim() || activeTemplate.label,
      type: activeTemplate.id,
      values,
      domain_id: domain._id,
      attached_url: attachedUrl || '',
    });
  };

  if (view === 'browse') {
    return (
      <SchemaBrowse onPick={startNew} onCancel={backToList} hasSaved={!!savedQuery.data?.length} />
    );
  }

  if (view === 'edit' && activeTemplate) {
    return (
      <SchemaEditor
        domain={domain}
        template={activeTemplate}
        values={values}
        setValues={setValues}
        name={name}
        setName={setName}
        attachedUrl={attachedUrl}
        setAttachedUrl={setAttachedUrl}
        generatedJson={generatedJson}
        errors={errors}
        saving={saveMutation.isPending}
        saveError={saveError}
        editingId={editingId}
        onSave={onSave}
        onCancel={backToList}
      />
    );
  }

  // ---- list (saved schemas) ----
  const saved = savedQuery.data || [];
  return (
    <div className="space-y-6">
      <div className="card-dark relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="absolute -bottom-20 -right-20 w-72 h-72 rounded-full bg-brand-500 blur-3xl opacity-30" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-white/60 mb-1 flex items-center gap-1.5">
              <Braces className="w-3 h-3" /> Schema library
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">JSON-LD, no copy-paste required</h2>
            <p className="text-sm text-white/70 mt-1 max-w-xl leading-relaxed">
              Fill in a form, get production-ready JSON-LD structured data. Paste it into your
              page&apos;s <code className="bg-white/10 rounded px-1 py-0.5">&lt;head&gt;</code>{' '}
              — or auto-inject it on a WordPress site via our plugin. AI engines and Google
              parse the same way.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="pill bg-white/10 text-white/80 backdrop-blur">
                <Sparkles className="w-3 h-3" /> 15 templates
              </span>
              <span className="pill bg-white/10 text-white/80 backdrop-blur">
                LocalBusiness · FAQ · Article · Product · …
              </span>
            </div>
          </div>
          <button
            className="inline-flex items-center gap-2 rounded-lg bg-white text-ink-900 px-4 py-2 text-sm font-semibold hover:bg-white/90 transition-colors"
            onClick={() => setView('browse')}
          >
            <Plus className="w-4 h-4" /> New schema
          </button>
        </div>
      </div>

      {saved.length === 0 ? (
        <EmptyState
          icon={Braces}
          title="No schemas yet"
          body="Pick from 15 prebuilt templates — LocalBusiness, FAQ, Article, Product, and more. Generated JSON-LD is ready to paste into any page."
          action={
            <button className="btn-primary" onClick={() => setView('browse')}>
              <Plus className="w-4 h-4" /> Browse templates
            </button>
          }
        />
      ) : (
        <div className="card">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <Layers className="w-4 h-4" />
              </span>
              Saved schemas
            </div>
            <span className="text-xs text-ink-400">
              {saved.length} {saved.length === 1 ? 'schema' : 'schemas'}
            </span>
          </div>
          <ul className="divide-y divide-gray-50">
            {saved.map((s) => {
              const tpl = getTemplate(s.type);
              const Icon = iconFor(tpl?.icon);
              return (
                <li
                  key={s._id}
                  className="py-3 flex items-center justify-between gap-3 first:pt-0 last:pb-0"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="flex-none h-9 w-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="font-medium text-ink-900 truncate">{s.name}</div>
                      <div className="text-xs text-ink-400 truncate">
                        {tpl?.label || s.type}
                        {s.attached_url ? ` · ${s.attached_url}` : ''} · updated{' '}
                        {formatRelative(s.updatedAt)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-none">
                    <button className="btn-icon" title="Edit" onClick={() => startEdit(s)}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      className="btn-icon hover:!text-rose-600 hover:!bg-rose-50"
                      title="Delete"
                      onClick={() => {
                        if (window.confirm(`Delete "${s.name}"?`)) {
                          deleteMutation.mutate(s._id);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function SchemaBrowse({ onPick, onCancel, hasSaved }) {
  const grouped = useMemo(() => {
    const m = new Map();
    for (const t of SCHEMA_TEMPLATES) {
      if (!m.has(t.category)) m.set(t.category, []);
      m.get(t.category).push(t);
    }
    return m;
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-ink-900 flex items-center gap-2">
          <Braces className="w-5 h-5 text-brand-600" /> Pick a schema template
        </h2>
        {hasSaved && (
          <button className="btn-secondary" onClick={onCancel}>
            <ArrowLeft className="w-4 h-4" /> Back to saved
          </button>
        )}
      </div>
      {CATEGORY_ORDER.map((cat) => {
        const items = grouped.get(cat);
        if (!items) return null;
        return (
          <div key={cat}>
            <div className="text-xs uppercase tracking-wider font-semibold text-ink-400 mb-3">
              {cat}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((t) => {
                const Icon = iconFor(t.icon);
                return (
                  <button
                    key={t.id}
                    onClick={() => onPick(t.id)}
                    className="card-hover text-left p-4 group"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className="flex-none h-10 w-10 rounded-xl bg-gradient-to-br from-brand-50 to-cyan-500/10 text-brand-700 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-5 h-5" />
                      </span>
                      <div className="font-semibold text-ink-900">{t.label}</div>
                    </div>
                    <p className="text-xs text-ink-400 leading-relaxed">{t.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SchemaEditor({
  domain,
  template,
  values,
  setValues,
  name,
  setName,
  attachedUrl,
  setAttachedUrl,
  generatedJson,
  errors,
  saving,
  saveError,
  editingId,
  onSave,
  onCancel,
}) {
  const [copied, setCopied] = useState(false);
  const Icon = iconFor(template.icon);
  const valid = errors.length === 0;
  const scriptTag = `<script type="application/ld+json">\n${generatedJson}\n</script>`;

  const onCopy = async () => {
    await navigator.clipboard.writeText(scriptTag);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const onDownload = () => {
    const blob = new Blob([scriptTag], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = (name || template.id).replace(/[^a-z0-9-_]+/gi, '-').toLowerCase();
    a.download = `${safeName}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Editor header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <button className="btn-icon" onClick={onCancel} title="Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="flex-none h-10 w-10 rounded-xl bg-gradient-to-br from-brand-50 to-cyan-500/10 text-brand-700 flex items-center justify-center">
            <Icon className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-ink-400">
              {editingId ? 'Editing' : 'New schema'}
            </div>
            <div className="font-semibold text-ink-900 truncate">{template.label}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary"
            onClick={onCopy}
            disabled={!valid}
            title={valid ? 'Copy script tag' : 'Fix validation errors first'}
          >
            {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy <script>'}
          </button>
          <button className="btn-secondary" onClick={onDownload} disabled={!valid}>
            <Download className="w-4 h-4" /> Download
          </button>
          <button className="btn-primary" onClick={onSave} disabled={!valid || saving}>
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : editingId ? 'Update' : 'Save'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="card">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <Pencil className="w-4 h-4" />
              </span>
              Fields
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="label">Schema name (internal)</label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={`${template.label} – ${domain.domain}`}
              />
            </div>
            <div>
              <label className="label">Attached page URL (optional)</label>
              <input
                className="input"
                value={attachedUrl}
                onChange={(e) => setAttachedUrl(e.target.value)}
                placeholder={`https://${domain.domain}/your-page`}
              />
              <p className="text-xs text-ink-400 mt-1.5">
                Lets you remember which page this schema is intended for.
              </p>
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-4">
              {template.fields.map((field) => (
                <FieldRenderer
                  key={field.key}
                  field={field}
                  value={values[field.key]}
                  onChange={(v) => setValues({ ...values, [field.key]: v })}
                />
              ))}
            </div>
          </div>

          {(errors.length > 0 || saveError) && (
            <div className="mt-4 rounded-xl border border-rose-100 bg-rose-50 text-rose-700 text-sm p-3 space-y-1">
              {saveError && (
                <div className="flex items-center gap-2 font-semibold">
                  <AlertCircle className="w-4 h-4" /> {saveError}
                </div>
              )}
              {errors.map((e, i) => (
                <div key={i} className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-none mt-0.5" /> {e}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="card">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <Code2 className="w-4 h-4" />
              </span>
              JSON-LD preview
            </div>
            {valid ? (
              <span className="pill-success">
                <CheckCircle2 className="w-3 h-3" /> Valid
              </span>
            ) : (
              <span className="pill-warn">
                <AlertCircle className="w-3 h-3" /> {errors.length} issue
                {errors.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <pre className="text-xs bg-ink-900 text-emerald-200 rounded-xl p-4 overflow-auto max-h-[32rem] whitespace-pre-wrap scroll-soft border border-ink-800 leading-relaxed">
            {scriptTag}
          </pre>
          <p className="text-xs text-ink-400 mt-3">
            Paste this snippet just before <code>&lt;/head&gt;</code> on the page above. Schema is
            re-parsed on the next AI crawl — no need to redeploy anything else.
          </p>
        </div>
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange }) {
  const labelEl = (
    <label className="label flex items-center gap-2">
      <span>{field.label}</span>
      {field.required ? (
        <span className="text-rose-500" title="Required">
          *
        </span>
      ) : (
        <span className="text-[10px] font-medium uppercase tracking-wide text-ink-400">
          optional
        </span>
      )}
    </label>
  );

  if (field.type === 'array') {
    const items = Array.isArray(value) ? value : [];
    const addItem = () => {
      const blank = {};
      for (const sub of field.itemFields) blank[sub.key] = sub.default ?? '';
      onChange([...items, blank]);
    };
    const updateItem = (idx, sub, v) =>
      onChange(items.map((it, i) => (i === idx ? { ...it, [sub]: v } : it)));
    const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));

    return (
      <div>
        {labelEl}
        {field.help && <p className="text-xs text-ink-400 -mt-1 mb-2">{field.help}</p>}
        <div className="space-y-2">
          {items.length === 0 && (
            <p className="text-xs text-ink-400 italic">No entries yet.</p>
          )}
          {items.map((item, idx) => (
            <div
              key={idx}
              className="rounded-xl border border-gray-100 bg-gray-50/50 p-3 relative"
            >
              <button
                type="button"
                className="absolute top-2 right-2 btn-icon h-7 w-7 hover:!text-rose-600 hover:!bg-rose-50"
                onClick={() => removeItem(idx)}
                title="Remove"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-400 mb-2">
                Item #{idx + 1}
              </div>
              <div className="space-y-2">
                {field.itemFields.map((sub) => (
                  <FieldRenderer
                    key={sub.key}
                    field={sub}
                    value={item[sub.key]}
                    onChange={(v) => updateItem(idx, sub.key, v)}
                  />
                ))}
              </div>
            </div>
          ))}
          <button type="button" className="btn-secondary w-full" onClick={addItem}>
            <Plus className="w-4 h-4" /> Add item
          </button>
        </div>
      </div>
    );
  }

  const common = {
    className: 'input',
    value: value ?? '',
    onChange: (e) => onChange(e.target.value),
    placeholder: field.placeholder || '',
  };

  let control;
  if (field.type === 'textarea') {
    control = <textarea {...common} rows={3} className="input min-h-[80px]" />;
  } else if (field.type === 'select') {
    control = (
      <select {...common}>
        <option value="">— Select —</option>
        {field.options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  } else if (field.type === 'number') {
    control = (
      <input
        {...common}
        type="number"
        step="any"
        onChange={(e) =>
          onChange(e.target.value === '' ? '' : Number(e.target.value))
        }
      />
    );
  } else {
    const typeMap = {
      url: 'url',
      email: 'email',
      tel: 'tel',
      date: 'date',
      'datetime-local': 'datetime-local',
      text: 'text',
    };
    control = <input {...common} type={typeMap[field.type] || 'text'} />;
  }

  return (
    <div>
      {labelEl}
      {field.help && <p className="text-xs text-ink-400 -mt-1 mb-1.5">{field.help}</p>}
      {control}
    </div>
  );
}

